import React, { useState, useEffect } from "react";
import { i18nRegister } from "@markedesk/plugin-sdk/ui";
import {
  Facebook,
  Instagram,
  WhatsApp,
  Chat as ChatIcon,
} from "@material-ui/icons";
import TelegramIcon from "@mui/icons-material/Telegram";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SmsIcon from "@mui/icons-material/Sms";
import EmailIcon from "@mui/icons-material/Email";
import ForumIcon from "@mui/icons-material/Forum";
import WebhookIcon from "@mui/icons-material/Webhook";
import {
  runPluginScripts,
  warnOrphanSlotControllers,
  hasSlotController,
} from "@markedesk/plugin-sdk/ui";
import api from "../../services/api";

const iconMap = {
  WhatsApp,
  Facebook,
  Instagram,
  Telegram: TelegramIcon,
  SmartToy: SmartToyIcon,
  Webhook: WebhookIcon,
  Sms: SmsIcon,
  Email: EmailIcon,
  Forum: ForumIcon,
  Chat: ChatIcon,
};

export const nativeChannels = {
  whatsapp: { icon: "WhatsApp", color: "#25d366" },
  whatsapp_oficial: { icon: "WhatsApp", color: "#25d366" },
  facebook: { icon: "Facebook", color: "#3b5998" },
  instagram: { icon: "Instagram", color: "#e1306c" },
};

// Cache compartilhado entre todas as instâncias
let pluginsCache = null;
let pluginsPromise = null;

function fetchPlugins() {
  if (pluginsCache) return Promise.resolve(pluginsCache);
  if (pluginsPromise) return pluginsPromise;
  pluginsPromise = api
    .get("/plugins")
    .then(({ data }) => {
      pluginsCache = data;
      // Textos dos plugins no i18n, ANTES dos scripts: um frontendScript que
      // ja monta tela precisa das frases traduzidas na primeira renderizacao.
      try {
        data.forEach((p) => i18nRegister(p.id || p.channel, p?.provides?.i18n));
      } catch (e) { /* ignore */ }
      // Executa os scripts de UI dos plugins (provides.frontendScript)
      try {
        runPluginScripts(data);
      } catch (e) { /* ignore */ }
      // Em desenvolvimento, denuncia controlador que nenhum ponto vai executar —
      // a falha que antes era MUDA (o plugin registrava, ninguém chamava, e nada
      // indicava o problema). O atraso dá tempo de as telas montarem e se
      // anunciarem via markSlotMounted; slot de tela ainda não aberta não é
      // acusado (só o que a tela renderiza sem ligar o motor de wrap). Nome fora
      // do catálogo já é acusado no próprio registerSlot, sem depender disto.
      if (process.env.NODE_ENV !== "production") {
        setTimeout(() => {
          try { warnOrphanSlotControllers(); } catch (e) { /* ignore */ }
        }, 5000);
      }
      return data;
    })
    .catch(() => {
      // Falha NÃO vira cache. `pluginsCache = []` parecia inofensivo, mas `[]`
      // é truthy: o `if (pluginsCache)` lá em cima passava a devolver a lista
      // vazia para sempre, e como o app é SPA nada reexecuta este módulo.
      //
      // O caso real: entrar com a sessão expirada. O /plugins volta 401, o
      // cache é envenenado com [], o usuário faz login — e a partir dali NENHUM
      // plugin existe para o app até um F5 completo. Ícone de canal some da
      // lista de tickets, ação de plugin não aparece, e um overlay obrigatório
      // (onboarding) simplesmente não monta, sem erro nenhum no console.
      //
      // Zerando a promessa, a próxima montagem tenta de novo — que é o que
      // acontece assim que a sessão volta.
      pluginsPromise = null;
      return [];
    });
  return pluginsPromise;
}

const listeners = new Set();

export function clearPluginsCache() {
  pluginsCache = null;
  pluginsPromise = null;
  // Refetch e notificar todos os hooks
  fetchPlugins().then((data) => {
    listeners.forEach((fn) => fn(data));
  });
}

export function useChannelPlugins() {
  const [plugins, setPlugins] = useState(pluginsCache || []);

  useEffect(() => {
    // O cleanup tira o listener, mas não cancela a busca já em voo: quando o
    // componente desmonta antes de ela responder — o caso comum é a sessão
    // expirar, o /plugins voltar 401 e a árvore inteira desmontar —, o `.then`
    // chamava setState num componente que não existe mais, e o React acusava
    // "state update on unmounted component" apontando para quem usou o hook.
    let montado = true;
    fetchPlugins().then((lista) => {
      if (montado) setPlugins(lista);
    });

    listeners.add(setPlugins);
    return () => {
      montado = false;
      listeners.delete(setPlugins);
    };
  }, []);

  return plugins;
}

/**
 * true se algum plugin serve este slot — pelos DOIS motores.
 *
 * Olhar só `provides.actions` deixava de fora quem usa `registerSlot()`: o
 * gate dava false, o <PluginBridge> nunca era montado e o controlador do
 * plugin ficava registrado sem nunca executar — sem erro e sem log, que é
 * exatamente o sintoma que a unificação do PluginBridge veio resolver.
 *
 * Use em hot paths (ex: cada mensagem da lista) para só montar o
 * <PluginBridge> quando há plugin de fato — evita render à toa em centenas
 * de itens.
 */
export function useHasSlot(slot) {
  const plugins = useChannelPlugins();
  const temAction = plugins.some((p) =>
    (p.provides?.actions || []).some((a) => a.slot === slot)
  );
  return temAction || hasSlotController(slot);
}

export default function ChannelIcon({ channel, style = {} }) {
  const plugins = useChannelPlugins();

  const plugin = plugins.find((p) => p.channel === channel);

  if (plugin) {
    const IconComp = iconMap[plugin.icon] || ChatIcon;
    return <IconComp style={{ color: plugin.color, ...style }} />;
  }

  const native = nativeChannels[channel];
  if (native) {
    const IconComp = iconMap[native.icon];
    if (IconComp) return <IconComp style={{ color: native.color, ...style }} />;
  }

  return <ChatIcon style={{ color: "#999", ...style }} />;
}
